import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/cn";

/**
 * Variants renamed from shadcn's default/secondary/destructive/outline to
 * the semantic vocabulary docs/ui/design-system.md §11 specifies for
 * Badge/StatusPill — Success/Warning/Danger/Info/Neutral, mapped 1:1 to §2's
 * semantic colors — so feature code reaches for `variant="success"`, never
 * has to remember "which generic name means which status." Pill radius
 * (`rounded-full`) and `caption`-scale text replace shadcn's `rounded-md` +
 * `text-xs`, per §7 and §11.
 */
const badgeVariants = cva(
  "inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-caption transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
  {
    variants: {
      variant: {
        success: "border-transparent bg-success text-success-foreground",
        warning: "border-transparent bg-warning text-warning-foreground",
        danger: "border-transparent bg-destructive text-destructive-foreground",
        info: "border-transparent bg-info text-info-foreground",
        neutral: "border-transparent bg-muted text-muted-foreground",
      },
    },
    defaultVariants: {
      variant: "neutral",
    },
  },
);

export interface BadgeProps
  extends React.HTMLAttributes<HTMLDivElement>, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, ...props }: BadgeProps) {
  return <div className={cn(badgeVariants({ variant }), className)} {...props} />;
}

export { Badge, badgeVariants };
